import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import {
  formatAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"

/**
 * One product's share of the usage, for the split bar. The same shape works
 * for a single user (from the profile's AppMix) and for a whole scope
 * (summed over members) — the scope variant additionally carries seat
 * counts.
 */
export type UsageAppMixEntry = {
  app: string
  weighted28d: number
  events28d: number
  trendPct?: number | null
  lastActiveAt?: string | null
  /** Scope variant: members with activity in this app in the last 28 days. */
  activeSeats?: number
}

export type UsageAppMixProps = {
  entries: UsageAppMixEntry[]
  className?: string
}

// Product-mix split: which console generation the activity comes from.
// Customers buy Console v1 and v2 separately, so a sales/CS person reads
// this as product fit — a v1-heavy bar means the v2 pitch hasn't landed
// yet, a zero-width segment with history means usage of that product
// stopped. Zero-activity apps keep their row (that absence is the signal).
function UsageAppMix(props: UsageAppMixProps) {
  const { entries, className } = props

  const total = entries.reduce((sum, entry) => sum + entry.weighted28d, 0)
  if (entries.length === 0) return null

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {total > 0 && (
        <span className="flex h-2 overflow-hidden rounded-full bg-muted">
          {entries
            .filter((entry) => entry.weighted28d > 0)
            .map((entry) => (
              <span
                key={entry.app}
                className="block h-full"
                style={{
                  width: `${(entry.weighted28d / total) * 100}%`,
                  backgroundColor: getAppCodeColor(entry.app),
                }}
              />
            ))}
        </span>
      )}
      <ul className="flex flex-col gap-1">
        {entries.map((entry) => (
          <li key={entry.app} className="flex items-center gap-2 text-xs">
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: getAppCodeColor(entry.app) }}
            />
            <span className="w-24 shrink-0 font-medium">
              {formatAppCode(entry.app)}
            </span>
            <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">
              {total > 0
                ? `${Math.round((entry.weighted28d / total) * 100)}%`
                : "—"}
            </span>
            <span className="w-16 shrink-0 text-right tabular-nums text-muted-foreground">
              {formatNumber(Math.round(entry.weighted28d))}
            </span>
            <UsageTrendBadge
              trendPct={entry.trendPct}
              className="w-16 justify-center"
            />
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {entry.activeSeats != null && (
                <>
                  {entry.activeSeats} active seat
                  {entry.activeSeats === 1 ? "" : "s"}
                </>
              )}
              {entry.activeSeats == null && entry.lastActiveAt && (
                <>
                  last active <RelativeDate date={entry.lastActiveAt} />
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default UsageAppMix
